/**
 * Date utility functions
 */
import { format, parseISO, isValid, isBefore, differenceInDays } from 'date-fns';

/**
 * Format date to user-friendly string
 */
export const formatDate = (dateString?: string | null, formatStr = 'MMM dd, yyyy'): string => {
  if (!dateString) return 'N/A';
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    if (!isValid(date)) return 'Invalid Date';
    return format(date, formatStr);
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Format date and time
 */
export const formatDateTime = (dateString?: string | null): string => {
  return formatDate(dateString, 'MMM dd, yyyy HH:mm');
};

/**
 * Check if voucher is expired
 */
export const isExpired = (expiryDate?: string | null): boolean => {
  if (!expiryDate) return false;
  try {
    const expiry = parseISO(expiryDate);
    if (!isValid(expiry)) return false;
    return isBefore(expiry, new Date());
  } catch {
    return false;
  }
};

/**
 * Check if voucher is expiring soon (within 30 days)
 */
export const isExpiringSoon = (expiryDate?: string | null, days = 30): boolean => {
  if (!expiryDate) return false;
  try {
    const expiry = parseISO(expiryDate);
    if (!isValid(expiry)) return false;
    const today = new Date();
    const daysUntilExpiry = differenceInDays(expiry, today);
    return daysUntilExpiry >= 0 && daysUntilExpiry <= days;
  } catch {
    return false;
  }
};

/**
 * Get days until expiry
 */
export const getDaysUntilExpiry = (expiryDate?: string | null): number | null => {
  if (!expiryDate) return null;
  try {
    const expiry = parseISO(expiryDate);
    if (!isValid(expiry)) return null;
    const today = new Date();
    return differenceInDays(expiry, today);
  } catch {
    return null;
  }
};

/**
 * Format currency
 */
export const formatCurrency = (value: number, currency = 'INR'): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

